import { useState, useRef, useEffect, useCallback } from 'react';
import { useVisualEffects, type VisualEffectsSettings } from '../../contexts/VisualEffectsContext';
import { clearPlaylistHistory } from '../../lib/playlist-history';

const TOGGLES: { key: keyof VisualEffectsSettings; label: string }[] = [
  { key: 'particles', label: 'Particles' },
  { key: 'glowEffects', label: 'Glow Effects' },
  { key: 'enhancedAnimations', label: 'Animations' },
];

export function SettingsDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [historyCleared, setHistoryCleared] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { settings, setSetting } = useVisualEffects();

  const handleClearHistory = useCallback(() => {
    clearPlaylistHistory();
    setHistoryCleared(true);
  }, []);

  // Reset confirmation message after 2s
  useEffect(() => {
    if (!historyCleared) return;
    const timer = setTimeout(() => setHistoryCleared(false), 2000);
    return () => clearTimeout(timer);
  }, [historyCleared]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg transition-colors text-parchment-500 hover:text-parchment-text hover:bg-black/5 dark:text-white/60 dark:hover:text-white dark:hover:bg-white/10"
        aria-label="Settings"
        aria-expanded={isOpen}
      >
        {/* Gear icon */}
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 rounded-lg bg-white dark:bg-earth-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 py-2 z-50 origin-top-right animate-scale-in">
          <div className="px-4 py-2 text-xs font-medium text-parchment-400 dark:text-white/50 uppercase tracking-wider animate-item-enter" style={{ animationDelay: '0ms' }}>
            Visual Effects
          </div>

          {TOGGLES.map((toggle, i) => (
            <label
              key={toggle.key}
              className="flex items-center justify-between px-4 py-2 hover:bg-parchment-100 dark:hover:bg-white/5 cursor-pointer animate-item-enter"
              style={{ animationDelay: `${(i + 1) * 50}ms` }}
            >
              <span className="text-sm text-parchment-text/80 dark:text-white/80">{toggle.label}</span>
              <Toggle checked={settings[toggle.key]} onChange={(checked) => setSetting(toggle.key, checked)} />
            </label>
          ))}

          <div className="my-2 border-t border-parchment-200 dark:border-white/10 animate-item-enter" style={{ animationDelay: `${(TOGGLES.length + 1) * 50}ms` }} />

          <div className="px-4 py-2 text-xs font-medium text-parchment-400 dark:text-white/50 uppercase tracking-wider animate-item-enter" style={{ animationDelay: `${(TOGGLES.length + 2) * 50}ms` }}>
            Data
          </div>

          <div className="px-4 py-2 animate-item-enter" style={{ animationDelay: `${(TOGGLES.length + 3) * 50}ms` }}>
            {historyCleared ? (
              <span className="text-sm text-green-600 dark:text-green-400">History cleared</span>
            ) : (
              <button
                onClick={handleClearHistory}
                className="text-sm text-parchment-text/80 dark:text-white/80 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              >
                Clear Track History
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function Toggle({ checked, onChange }: ToggleProps) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`
        relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full
        transition-colors duration-200 ease-in-out
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
        ${checked ? 'bg-primary-500' : 'bg-parchment-200 dark:bg-white/20'}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm
          transform transition-transform duration-200 ease-in-out
          ${checked ? 'translate-x-4' : 'translate-x-0.5'}
          mt-0.5
        `}
      />
    </button>
  );
}
